import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Export/Import Service for ZenBidTracker
 * Handles CSV and Excel export/import with data validation
 */

// === EXPORT FUNCTIONS ===

/**
 * Export bids to CSV format
 * @param {Array} bids - Array of bid objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {string} bidView - 'incoming' or 'standalone' to determine columns
 */
export const exportToCSV = (bids, filename = 'bids_export', bidView = 'incoming') => {
  if (!bids || bids.length === 0) {
    throw new Error('No bids to export');
  }

  // Define column headers based on bid view
  const headers = bidView === 'incoming' ? [
    'Project #',
    'Client',
    'Project Name',
    'Proposed Amount',
    'Date Issued',
    'Date Due',
    'Status',
    'Assigned To',
    'Contact',
    'Notes'
  ] : [
    'Project #',
    'Client',
    'Project Name',
    'Description',
    'Proposed Amount',
    'Date Due',
    'Date Issued',
    'Status',
    'Assigned To',
    'Contact',
    'Notes'
  ];

  // Convert bids to CSV rows
  const rows = bids.map(bid => bidView === 'incoming' ? [
    bid.JobNumber || '',
    bid.ClientName || '',
    bid.JobName || '',
    bid.BidAmount || '',
    bid.DateIssued || '',
    bid.DateDue || '',
    bid.BidStatus || '',
    bid.AssignedTo || '',
    bid.Contact || '',
    bid.Notes || ''
  ] : [
    bid.JobNumber || '',
    bid.ClientName || '',
    bid.JobName || '',
    bid.JobDescription || '',
    bid.BidAmount || '',
    bid.DateDue || '',
    bid.DateIssued || '',
    bid.BidStatus || '',
    bid.AssignedTo || '',
    bid.Contact || '',
    bid.Notes || ''
  ]);

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
};

/**
 * Export bids to Excel format with formatting
 * @param {Array} bids - Array of bid objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {string} bidView - 'incoming' or 'standalone' to determine columns
 */
export const exportToExcel = async (bids, filename = 'bids_export', bidView = 'incoming') => {
  if (!bids || bids.length === 0) {
    throw new Error('No bids to export');
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Bids');

  // Set column definitions with widths based on bid view
  worksheet.columns = bidView === 'incoming' ? [
    { header: 'Project #', key: 'jobNumber', width: 15 },
    { header: 'Client', key: 'client', width: 25 },
    { header: 'Project Name', key: 'projectName', width: 30 },
    { header: 'Proposed Amount', key: 'bidAmount', width: 18 },
    { header: 'Date Issued', key: 'dateIssued', width: 15 },
    { header: 'Date Due', key: 'dateDue', width: 15 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Assigned To', key: 'assignedTo', width: 20 },
    { header: 'Contact', key: 'contact', width: 20 },
    { header: 'Notes', key: 'notes', width: 40 }
  ] : [
    { header: 'Project #', key: 'jobNumber', width: 15 },
    { header: 'Client', key: 'client', width: 25 },
    { header: 'Project Name', key: 'projectName', width: 30 },
    { header: 'Description', key: 'description', width: 35 },
    { header: 'Proposed Amount', key: 'bidAmount', width: 18 },
    { header: 'Date Due', key: 'dateDue', width: 15 },
    { header: 'Date Issued', key: 'dateIssued', width: 15 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Assigned To', key: 'assignedTo', width: 20 },
    { header: 'Contact', key: 'contact', width: 20 },
    { header: 'Notes', key: 'notes', width: 40 }
  ];

  // Style the header row
  worksheet.getRow(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(1).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF3498db' }
  };
  worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
  worksheet.getRow(1).height = 20;

  // Add data rows
  bids.forEach((bid) => {
    const rowData = bidView === 'incoming' ? {
      jobNumber: bid.JobNumber || '',
      client: bid.ClientName || '',
      projectName: bid.JobName || '',
      bidAmount: bid.BidAmount || 0,
      dateIssued: bid.DateIssued || '',
      dateDue: bid.DateDue || '',
      status: bid.BidStatus || '',
      assignedTo: bid.AssignedTo || '',
      contact: bid.Contact || '',
      notes: bid.Notes || ''
    } : {
      jobNumber: bid.JobNumber || '',
      client: bid.ClientName || '',
      projectName: bid.JobName || '',
      description: bid.JobDescription || '',
      bidAmount: bid.BidAmount || 0,
      dateDue: bid.DateDue || '',
      dateIssued: bid.DateIssued || '',
      status: bid.BidStatus || '',
      assignedTo: bid.AssignedTo || '',
      contact: bid.Contact || '',
      notes: bid.Notes || ''
    };

    const row = worksheet.addRow(rowData);

    // Format currency columns
    row.getCell('bidAmount').numFmt = '$#,##0.00';

    // Color code status
    const statusColors = {
      'Open': 'FF3498db',
      'Submitted': 'FFf39c12',
      'Awarded': 'FF2ecc71',
      'Lost': 'FFe74c3c',
      'On Hold': 'FF95a5a6'
    };

    const statusCell = row.getCell('status');
    if (statusColors[bid.BidStatus]) {
      statusCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: statusColors[bid.BidStatus] }
      };
      statusCell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      statusCell.alignment = { horizontal: 'center' };
    }
  });

  // Add borders to all cells
  worksheet.eachRow((row, rowNumber) => {
    row.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFcccccc' } },
        left: { style: 'thin', color: { argb: 'FFcccccc' } },
        bottom: { style: 'thin', color: { argb: 'FFcccccc' } },
        right: { style: 'thin', color: { argb: 'FFcccccc' } }
      };
    });
  });

  // Add summary row at the bottom
  const summaryRow = worksheet.addRow({});
  const amountColumnIndex = bidView === 'incoming' ? 4 : 5;
  const amountColumnLetter = bidView === 'incoming' ? 'D' : 'E';

  summaryRow.getCell(3).value = 'TOTALS:';
  summaryRow.getCell(3).font = { bold: true };
  summaryRow.getCell(amountColumnIndex).value = {
    formula: `SUM(${amountColumnLetter}2:${amountColumnLetter}${bids.length + 1})`,
    result: bids.reduce((sum, bid) => sum + (Number(bid.BidAmount) || 0), 0)
  };
  summaryRow.getCell(amountColumnIndex).numFmt = '$#,##0.00';
  summaryRow.getCell(amountColumnIndex).font = { bold: true };

  // Generate and download file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// === IMPORT FUNCTIONS ===

/**
 * Parse CSV file content
 * @param {File} file - CSV file to parse
 * @param {string} bidView - 'incoming' or 'standalone' to set bid type
 * @returns {Promise<Array>} Array of parsed bid objects
 */
export const parseCSVFile = (file, bidView = 'incoming') => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').map(line => line.trim()).filter(line => line);

        if (lines.length < 2) {
          reject(new Error('CSV file is empty or invalid'));
          return;
        }

        // Parse header row
        const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());

        // Parse data rows
        const bids = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);

          if (values.length !== headers.length) {
            console.warn(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}`);
          }

          const bid = bidView === 'incoming' ? {
            JobNumber: values[0] || '',
            ClientName: values[1] || '',
            JobName: values[2] || '',
            BidAmount: values[3] || '',
            DateIssued: values[4] || '',
            DateDue: values[5] || '',
            BidStatus: values[6] || 'Open',
            AssignedTo: values[7] || '',
            Contact: values[8] || '',
            Notes: values[9] || '',
            type: 'project'
          } : {
            JobNumber: values[0] || '',
            ClientName: values[1] || '',
            JobName: values[2] || '',
            JobDescription: values[3] || '',
            BidAmount: values[4] || '',
            DateDue: values[5] || '',
            DateIssued: values[6] || '',
            BidStatus: values[7] || 'Open',
            AssignedTo: values[8] || '',
            Contact: values[9] || '',
            Notes: values[10] || '',
            type: 'standalone'
          };

          bids.push(bid);
        }

        resolve(bids);
      } catch (error) {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Parse a single CSV line (handles quoted values with commas)
 */
const parseCSVLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // Skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
};

/**
 * Parse Excel file content
 * @param {File} file - Excel file to parse
 * @param {string} bidView - 'incoming' or 'standalone' to set bid type
 * @returns {Promise<Array>} Array of parsed bid objects
 */
export const parseExcelFile = async (file, bidView = 'incoming') => {
  try {
    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    const worksheet = workbook.getWorksheet(1); // Get first worksheet

    if (!worksheet) {
      throw new Error('Excel file has no worksheets');
    }

    const bids = [];
    let headerRow = null;

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) {
        headerRow = row.values;
        return;
      }

      // Skip empty rows and summary rows
      if (!row.getCell(1).value || row.getCell(3).value === 'TOTALS:') {
        return;
      }

      const bid = bidView === 'incoming' ? {
        JobNumber: String(row.getCell(1).value || ''),
        ClientName: String(row.getCell(2).value || ''),
        JobName: String(row.getCell(3).value || ''),
        BidAmount: String(row.getCell(4).value || ''),
        DateIssued: row.getCell(5).value || '',
        DateDue: row.getCell(6).value || '',
        BidStatus: String(row.getCell(7).value || 'Open'),
        AssignedTo: String(row.getCell(8).value || ''),
        Contact: String(row.getCell(9).value || ''),
        Notes: String(row.getCell(10).value || ''),
        type: 'project'
      } : {
        JobNumber: String(row.getCell(1).value || ''),
        ClientName: String(row.getCell(2).value || ''),
        JobName: String(row.getCell(3).value || ''),
        JobDescription: String(row.getCell(4).value || ''),
        BidAmount: String(row.getCell(5).value || ''),
        DateDue: row.getCell(6).value || '',
        DateIssued: row.getCell(7).value || '',
        BidStatus: String(row.getCell(8).value || 'Open'),
        AssignedTo: String(row.getCell(9).value || ''),
        Contact: String(row.getCell(10).value || ''),
        Notes: String(row.getCell(11).value || ''),
        type: 'standalone'
      };

      // Format dates if they're Excel date values
      if (bid.DateIssued instanceof Date) {
        bid.DateIssued = bid.DateIssued.toISOString().split('T')[0];
      }
      if (bid.DateDue instanceof Date) {
        bid.DateDue = bid.DateDue.toISOString().split('T')[0];
      }

      bids.push(bid);
    });

    return bids;
  } catch (error) {
    throw new Error(`Failed to parse Excel file: ${error.message}`);
  }
};

/**
 * Validate imported bid data
 * @param {Array} bids - Array of bid objects to validate
 * @returns {Object} Validation result with errors array
 */
export const validateImportedBids = (bids) => {
  const errors = [];
  const validatedBids = [];

  bids.forEach((bid, index) => {
    const rowNumber = index + 2; // +2 because of header row and 0-index
    const rowErrors = [];

    // Required fields validation
    if (!bid.ClientName || bid.ClientName.trim() === '') {
      rowErrors.push('Client name is required');
    }

    if (!bid.JobName || bid.JobName.trim() === '') {
      rowErrors.push('Project name is required');
    }

    // Validate status
    const validStatuses = ['Open', 'Submitted', 'Awarded', 'Lost', 'On Hold'];
    if (bid.BidStatus && !validStatuses.includes(bid.BidStatus)) {
      rowErrors.push(`Invalid status "${bid.BidStatus}". Must be one of: ${validStatuses.join(', ')}`);
    }

    // Validate bid amount (if provided)
    if (bid.BidAmount) {
      const amount = parseFloat(bid.BidAmount);
      if (isNaN(amount)) {
        rowErrors.push('Bid amount must be a valid number');
      } else if (amount < 0) {
        rowErrors.push('Bid amount cannot be negative');
      }
    }

    // Validate dates (if provided)
    if (bid.DateIssued && !isValidDate(bid.DateIssued)) {
      rowErrors.push('Date issued must be a valid date (YYYY-MM-DD)');
    }

    if (bid.DateDue && !isValidDate(bid.DateDue)) {
      rowErrors.push('Date due must be a valid date (YYYY-MM-DD)');
    }

    if (rowErrors.length > 0) {
      errors.push({
        row: rowNumber,
        errors: rowErrors,
        data: bid
      });
    } else {
      validatedBids.push(bid);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    validatedBids,
    totalRows: bids.length,
    validRows: validatedBids.length,
    errorRows: errors.length
  };
};

/**
 * Check if a string is a valid date
 */
const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};

/**
 * Download a sample CSV template
 * @param {string} bidView - 'incoming' or 'standalone' to determine columns
 */
export const downloadSampleTemplate = (bidView = 'incoming') => {
  const headers = bidView === 'incoming' ? [
    'Project #',
    'Client',
    'Project Name',
    'Proposed Amount',
    'Date Issued',
    'Date Due',
    'Status',
    'Assigned To',
    'Contact',
    'Notes'
  ] : [
    'Project #',
    'Client',
    'Project Name',
    'Description',
    'Proposed Amount',
    'Date Due',
    'Date Issued',
    'Status',
    'Assigned To',
    'Contact',
    'Notes'
  ];

  const sampleRow = bidView === 'incoming' ? [
    'PROJ-001',
    'Sample Client Inc',
    'Office Renovation',
    '150000',
    '2026-01-15',
    '2026-02-15',
    'Open',
    'John Doe',
    'jane@sampleclient.com',
    'Sample project for import template'
  ] : [
    'STANDALONE-001',
    'Sample Client Inc',
    'Office Renovation',
    'Complete office renovation including electrical, HVAC, and finishes',
    '150000',
    '2026-02-15',
    '2026-01-15',
    'Open',
    'John Doe',
    'jane@sampleclient.com',
    'Sample standalone bid for import template'
  ];

  const csvContent = [
    headers.join(','),
    sampleRow.map(cell => `"${cell}"`).join(',')
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const filename = bidView === 'incoming' ? 'project_bid_template.csv' : 'standalone_bid_template.csv';
  saveAs(blob, filename);
};
